import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChartPie,
  faCheckCircle,
  faExclamationTriangle,
  faExternalLinkAlt,
  faFolderOpen,
  faSpinner,
  faEdit,
  faTrash,
  faBriefcase,
  faLink,
  faUnlink,
  faPlus,
  faTimesCircle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useWebSocket } from "@/context/WebSocketContext";
import { motion } from "motion/react";
import { ErrorBanner } from "@/components/ErrorBanner";
import Drawer from "@/components/ui/drawer";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import { Dialog, DialogTitle } from "@/components/ui/dialog/Dialog";
import { fetchScreenerSearch } from "@/services/api/screener";
import { OsLink } from "@/components/ui/os-layout";
import type {
  PortfolioDefinition,
  PortfolioDefinitionSummary,
  PortfolioHoldingV2,
} from "@/services/api/agentic/types";
import * as AgenticAPI from "@/services/api/agentic";

// ============================================
// Product Search Result Type
// ============================================

interface ProductSearchResult {
  resource_id: string;
  child_universe_id?: string;
  display_name: string;
  long_name?: string;
  isin?: string;
  currency?: string;
  resource_type: string;
}

// ============================================
// Types
// ============================================

export interface PortfoliosProps {
  entityId: string;
  entityType: string;
  websocketMessageType?: string;
  disableDrawer?: boolean;
}

// ============================================
// Formatting Utilities
// ============================================

const formatCurrency = (
  value: number | string | undefined,
  currency = "GBP"
): string => {
  if (value === undefined || value === null) return "—";
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return String(value);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
};

const formatPercentage = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return "—";
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return String(value);
  // Weights are stored as percentage values (e.g., 1.33 = 1.33%, 0.32 = 0.32%)
  // Intl.NumberFormat with style: "percent" multiplies by 100, so we divide first
  const displayValue = numValue / 100;
  return new Intl.NumberFormat("en-GB", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(displayValue);
};

const formatRelativeDate = (dateString?: string): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// ============================================
// Sub-Components
// ============================================

interface MatchStatusBadgeProps {
  matched: number;
  total: number;
}

function MatchStatusBadge({ matched, total }: MatchStatusBadgeProps) {
  const percentage = total > 0 ? (matched / total) * 100 : 0;

  if (percentage === 100) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
        All matched
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
      {matched}/{total} matched
    </span>
  );
}

interface StatusBadgeProps {
  status: string;
  portfolioId?: string | null;
}

function StatusBadge({ status, portfolioId }: StatusBadgeProps) {
  if (portfolioId) {
    return (
      <OsLink
        to={`/portfolio/${portfolioId}`}
        className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline dark:text-purple-400"
      >
        <FontAwesomeIcon icon={faFolderOpen} className="h-3 w-3" />
        Portfolio Created
        <FontAwesomeIcon icon={faExternalLinkAlt} className="h-2.5 w-2.5" />
      </OsLink>
    );
  }

  switch (status) {
    case "matched":
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          Ready to create
        </span>
      );
    case "pending":
    default:
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
          Pending
        </span>
      );
  }
}

// ============================================
// Holdings Table Component
// ============================================

interface HoldingsTableProps {
  holdings: PortfolioHoldingV2[];
  baseCurrency?: string;
  onMatchHolding?: (holdingId: string) => void;
  onEditFootnote?: (holdingId: string, currentFootnote: string) => void;
  searchQuery?: string;
}

function HoldingsTable({
  holdings,
  baseCurrency = "GBP",
  onMatchHolding,
  onEditFootnote,
  searchQuery,
}: HoldingsTableProps) {
  const filteredHoldings = useMemo(() => {
    if (!searchQuery) return holdings;
    const q = searchQuery.toLowerCase();
    return holdings.filter((h) => {
      const name = (h.name || h.fund_name || "").toLowerCase();
      const isin = (h.isin || "").toLowerCase();
      return name.includes(q) || isin.includes(q);
    });
  }, [holdings, searchQuery]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalValue = holdings.reduce((sum, h) => {
      const val =
        typeof h.value === "number"
          ? h.value
          : typeof h.value === "string"
            ? parseFloat(h.value) || 0
            : 0;
      return sum + val;
    }, 0);
    const totalWeight = holdings.reduce((sum, h) => {
      const wt =
        typeof h.weight === "number"
          ? h.weight
          : typeof h.weight === "string"
            ? parseFloat(h.weight) || 0
            : 0;
      return sum + wt;
    }, 0);
    return { totalValue, totalWeight };
  }, [holdings]);

  if (!holdings || holdings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No holdings found
      </div>
    );
  }

  if (filteredHoldings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No holdings matching &quot;{searchQuery}&quot;
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider py-3 px-4">
              Fund
            </th>
            <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider py-3 px-4 w-28">
              Value
            </th>
            <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider py-3 px-4 w-24">
              Weight
            </th>
            <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider py-3 px-4">
              Matched Product
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {filteredHoldings.map((holding, idx) => {
            const mp = holding.matched_product;
            const hasMatch = mp?.child_universe_id;
            const name =
              holding.name ||
              holding.fund_name ||
              holding.holding_name ||
              "Unknown";

            return (
              <tr
                key={holding.id || idx}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors group"
              >
                {/* Fund Name + ISIN */}
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {name}
                  </div>
                  {holding.isin && (
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {holding.isin}
                    </span>
                  )}
                </td>

                {/* Value */}
                <td className="py-3 px-4 text-right tabular-nums text-gray-700 dark:text-gray-300">
                  {holding.value !== undefined && holding.value !== null
                    ? formatCurrency(holding.value, baseCurrency)
                    : "—"}
                </td>

                {/* Weight */}
                <td className="py-3 px-4 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
                  {holding.weight !== undefined && holding.weight !== null
                    ? formatPercentage(holding.weight)
                    : "—"}
                </td>

                {/* Matched Product */}
                <td className="py-3 px-4">
                  {hasMatch ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <OsLink
                          to={`/product/${mp.child_universe_id}`}
                          className="inline-flex items-center gap-1.5 text-sm text-green-600 hover:underline dark:text-green-400"
                        >
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="h-4 w-4 flex-shrink-0"
                          />
                          <span>{mp.name || "Matched"}</span>
                          <FontAwesomeIcon
                            icon={faExternalLinkAlt}
                            className="h-3 w-3 opacity-50 flex-shrink-0"
                          />
                        </OsLink>
                        <button
                          onClick={() => onMatchHolding?.(holding.id)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          title="Edit match"
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            className="h-3.5 w-3.5"
                          />
                        </button>
                      </div>
                      {/* Display footnote (falls back to reasoning from LLM) */}
                      {(holding.footnote || mp.reasoning) && (
                        <div className="flex items-start gap-1 mt-1 group/footnote">
                          <span className="text-xs text-gray-500 dark:text-gray-400 italic flex-1">
                            {holding.footnote || mp.reasoning}
                          </span>
                          {onEditFootnote && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditFootnote(
                                  holding.id,
                                  holding.footnote || mp.reasoning || ""
                                );
                              }}
                              className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover/footnote:opacity-100 transition-opacity flex-shrink-0"
                              title="Edit footnote"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3"
                              />
                            </button>
                          )}
                        </div>
                      )}
                      {/* Add footnote button if none exists */}
                      {!holding.footnote && !mp.reasoning && onEditFootnote && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditFootnote(holding.id, "");
                          }}
                          className="text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          + Add footnote
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => onMatchHolding?.(holding.id)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                    >
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="h-4 w-4"
                      />
                      <span>Match manually</span>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Total Row */}
        <tfoot>
          <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
            <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">
              Total
            </td>
            <td className="py-3 px-4 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
              {formatCurrency(totals.totalValue, baseCurrency)}
            </td>
            <td className="py-3 px-4 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
              {formatPercentage(totals.totalWeight)}
            </td>
            <td className="py-3 px-4"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ============================================
// Portfolio Detail Drawer
// ============================================

interface PortfolioDrawerProps {
  portfolioDefId: string | null;
  onClose: () => void;
  onDeleted?: () => void;
  onUpdated?: () => void;
}

function PortfolioDrawer({
  portfolioDefId,
  onClose,
  onDeleted,
  onUpdated,
}: PortfolioDrawerProps) {
  const [portfolio, setPortfolio] = useState<PortfolioDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchingHoldingId, setMatchingHoldingId] = useState<string | null>(
    null
  );
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [productSearchResults, setProductSearchResults] = useState<
    ProductSearchResult[]
  >([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductSearchResult | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchNote, setMatchNote] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Portfolio linking state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [portfolioSearchQuery, setPortfolioSearchQuery] = useState("");
  const [portfolioSearchResults, setPortfolioSearchResults] = useState<
    ProductSearchResult[]
  >([]);
  const [portfolioSearchLoading, setPortfolioSearchLoading] = useState(false);
  const [selectedLinkPortfolio, setSelectedLinkPortfolio] =
    useState<ProductSearchResult | null>(null);
  const [linkNote, setLinkNote] = useState("");
  const portfolioSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Footnote editing state
  const [footnoteDialogOpen, setFootnoteDialogOpen] = useState(false);
  const [editingFootnoteHoldingId, setEditingFootnoteHoldingId] = useState<
    string | null
  >(null);
  const [footnoteValue, setFootnoteValue] = useState("");
  const [footnoteSaving, setFootnoteSaving] = useState(false);

  // Portfolio link note editing state
  const [editLinkNoteDialogOpen, setEditLinkNoteDialogOpen] = useState(false);
  const [editLinkNoteValue, setEditLinkNoteValue] = useState("");

  useEffect(() => {
    if (!portfolioDefId) {
      setPortfolio(null);
      return;
    }

    const currentId = portfolioDefId;

    async function loadPortfolio() {
      setLoading(true);
      setError(null);
      try {
        const data =
          await AgenticAPI.getAgenticPortfolioDefinition(currentId);
        setPortfolio(data);
      } catch (err) {
        console.error("Error loading portfolio:", err);
        setError("Failed to load portfolio details");
      } finally {
        setLoading(false);
      }
    }

    loadPortfolio();
  }, [portfolioDefId]);

  const handleDelete = async () => {
    if (!portfolioDefId) return;
    try {
      await AgenticAPI.deleteAgenticPortfolioDefinition(portfolioDefId);
      setDeleteConfirmOpen(false);
      onClose();
      onDeleted?.();
    } catch (err) {
      console.error("Error deleting portfolio:", err);
    }
  };

  const openMatchDialog = (holdingId: string) => {
    setMatchingHoldingId(holdingId);
    setProductSearchQuery("");
    setProductSearchResults([]);
    setSelectedProduct(null);
    // Pre-fill existing reasoning if editing
    const existingHolding = portfolio?.holdings?.find(
      (h) => h.id === holdingId
    );
    setMatchNote(existingHolding?.matched_product?.reasoning || "");
    setMatchDialogOpen(true);
  };

  // Product search with debounce
  const searchProducts = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }

    setProductSearchLoading(true);
    try {
      const response = await fetchScreenerSearch(
        query,
        "display_name",
        "asc",
        1,
        20,
        ["product"] // Products only, no benchmarks
      );

      const products: ProductSearchResult[] = (response.items || []).map(
        (item: any) => ({
          resource_id: item.metadata?.resource_id || item.id,
          child_universe_id: item.metadata?.child_universe_id || item.id,
          display_name: item.display_name,
          long_name:
            item.data?.long_name ??
            item.data?.portfolio_name ??
            item.fundamental_data?.long_name ??
            item.metadata?.long_name ??
            item.long_name,
          isin: item.data?.isin || item.fundamental_data?.isin,
          currency: item.currency || item.data?.currency,
          resource_type: item.resource_type,
        })
      );

      setProductSearchResults(products);
    } catch (error) {
      console.error("Error searching products:", error);
      setProductSearchResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, []);

  // Debounced product search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(productSearchQuery);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [productSearchQuery, searchProducts]);

  const handleSelectProduct = (product: ProductSearchResult) => {
    setSelectedProduct(product);
    setProductSearchQuery("");
    setProductSearchResults([]);
  };

  // Portfolio search for linking
  const searchPortfolios = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setPortfolioSearchResults([]);
      return;
    }

    setPortfolioSearchLoading(true);
    try {
      const response = await fetchScreenerSearch(
        query,
        "display_name",
        "asc",
        1,
        20,
        ["portfolio"] // Portfolios only
      );

      const portfolios: ProductSearchResult[] = (response.items || []).map(
        (item: any) => ({
          resource_id: item.metadata?.resource_id || item.id,
          display_name: item.display_name,
          long_name: item.data?.long_name,
          currency: item.currency || item.data?.currency,
          resource_type: item.resource_type,
        })
      );

      setPortfolioSearchResults(portfolios);
    } catch (error) {
      console.error("Error searching portfolios:", error);
      setPortfolioSearchResults([]);
    } finally {
      setPortfolioSearchLoading(false);
    }
  }, []);

  // Debounced portfolio search
  useEffect(() => {
    if (portfolioSearchTimeoutRef.current) {
      clearTimeout(portfolioSearchTimeoutRef.current);
    }
    portfolioSearchTimeoutRef.current = setTimeout(() => {
      searchPortfolios(portfolioSearchQuery);
    }, 300);
    return () => {
      if (portfolioSearchTimeoutRef.current) {
        clearTimeout(portfolioSearchTimeoutRef.current);
      }
    };
  }, [portfolioSearchQuery, searchPortfolios]);

  const handleSelectLinkPortfolio = (p: ProductSearchResult) => {
    setSelectedLinkPortfolio(p);
    setPortfolioSearchQuery("");
    setPortfolioSearchResults([]);
  };

  const handleConfirmLinkPortfolio = async () => {
    if (!portfolioDefId || !selectedLinkPortfolio) return;
    try {
      // Update the portfolio definition with the linked portfolio_id, name, and note
      const updated = await AgenticAPI.updateAgenticPortfolioDefinition(
        portfolioDefId,
        {
          portfolio_id: selectedLinkPortfolio.resource_id,
          linked_portfolio_name: selectedLinkPortfolio.display_name,
          linked_portfolio_note: linkNote || null,
        }
      );
      setPortfolio(updated);
      setLinkDialogOpen(false);
      setSelectedLinkPortfolio(null);
      setLinkNote("");
      onUpdated?.(); // Refresh main list
    } catch (err) {
      console.error("Error linking portfolio:", err);
    }
  };

  // Open footnote edit dialog
  const openFootnoteDialog = (holdingId: string, currentFootnote: string) => {
    setEditingFootnoteHoldingId(holdingId);
    setFootnoteValue(currentFootnote);
    setFootnoteDialogOpen(true);
  };

  // Save footnote
  const handleSaveFootnote = async () => {
    if (!portfolioDefId || !editingFootnoteHoldingId) return;
    setFootnoteSaving(true);
    try {
      const updated = await AgenticAPI.updateAgenticHoldingFootnote(
        portfolioDefId,
        editingFootnoteHoldingId,
        footnoteValue || null
      );
      setPortfolio(updated);
      setFootnoteDialogOpen(false);
      setEditingFootnoteHoldingId(null);
      setFootnoteValue("");
    } catch (err) {
      console.error("Error saving footnote:", err);
    } finally {
      setFootnoteSaving(false);
    }
  };

  // Open edit link note dialog
  const openEditLinkNoteDialog = () => {
    setEditLinkNoteValue(portfolio?.linked_portfolio_note || "");
    setEditLinkNoteDialogOpen(true);
  };

  // Save link note
  const handleSaveLinkNote = async () => {
    if (!portfolioDefId) return;
    try {
      const updated = await AgenticAPI.updateAgenticPortfolioDefinition(
        portfolioDefId,
        {
          linked_portfolio_note: editLinkNoteValue || null,
        }
      );
      setPortfolio(updated);
      setEditLinkNoteDialogOpen(false);
    } catch (err) {
      console.error("Error saving link note:", err);
    }
  };

  const handleUnlinkPortfolio = async () => {
    if (!portfolioDefId) return;
    try {
      const updated = await AgenticAPI.updateAgenticPortfolioDefinition(
        portfolioDefId,
        {
          portfolio_id: null,
          linked_portfolio_name: null,
          linked_portfolio_note: null,
        }
      );
      setPortfolio(updated);
      onUpdated?.(); // Refresh main list
    } catch (err) {
      console.error("Error unlinking portfolio:", err);
    }
  };

  const handleMatchHolding = async () => {
    if (!portfolioDefId || !matchingHoldingId || !selectedProduct) return;

    setMatchLoading(true);
    try {
      const updated = await AgenticAPI.matchAgenticPortfolioHolding(
        portfolioDefId,
        matchingHoldingId,
        {
          child_universe_id:
            selectedProduct.child_universe_id || selectedProduct.resource_id,
          isin: selectedProduct.isin,
          base_currency: selectedProduct.currency,
          reasoning: matchNote || `Manual match by user`,
        }
      );
      setPortfolio(updated);
      setMatchDialogOpen(false);
      setMatchNote("");
      onUpdated?.();
    } catch (err) {
      console.error("Error matching holding:", err);
    } finally {
      setMatchLoading(false);
    }
  };

  const matchedCount =
    portfolio?.holdings?.filter((h) => h.matched_product?.child_universe_id)
      .length || 0;
  const totalCount = portfolio?.holdings?.length || 0;

  return (
    <>
      <Drawer
        isOpen={!!portfolioDefId}
        onClose={onClose}
        title={portfolio?.name || "Portfolio Details"}
        width="max-w-5xl"
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <FontAwesomeIcon
              icon={faSpinner}
              className="h-6 w-6 animate-spin text-slate-400 dark:text-slate-500"
            />
          </div>
        ) : error ? (
          <ErrorBanner message={error} />
        ) : portfolio ? (
          <div className="px-4 sm:px-6 pb-6 space-y-6">
            {/* Linked Portfolio Section */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <FontAwesomeIcon
                    icon={faLink}
                    className="h-5 w-5 text-purple-600 dark:text-purple-400"
                  />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Linked Portfolio
                  </div>
                  {portfolio.portfolio_id ? (
                    <div className="group/linknote">
                      <OsLink
                        to={`/portfolio/${portfolio.portfolio_id}`}
                        className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
                      >
                        {portfolio.linked_portfolio_name || "View Portfolio"}
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="h-3 w-3"
                        />
                      </OsLink>
                      <div className="flex items-start gap-1 mt-0.5">
                        {portfolio.linked_portfolio_note ? (
                          <>
                            <span className="text-xs text-gray-500 dark:text-gray-400 italic flex-1">
                              {portfolio.linked_portfolio_note}
                            </span>
                            <button
                              onClick={openEditLinkNoteDialog}
                              className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover/linknote:opacity-100 transition-opacity flex-shrink-0"
                              title="Edit note"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3"
                              />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={openEditLinkNoteDialog}
                            className="text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 opacity-0 group-hover/linknote:opacity-100 transition-opacity"
                          >
                            + Add note
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Not linked — link to an existing portfolio or create new
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {portfolio.portfolio_id ? (
                  <Button
                    outline
                    icon={<FontAwesomeIcon icon={faUnlink} />}
                    onClick={handleUnlinkPortfolio}
                  >
                    Unlink
                  </Button>
                ) : (
                  <>
                    <Button
                      outline
                      icon={<FontAwesomeIcon icon={faLink} />}
                      onClick={() => setLinkDialogOpen(true)}
                    >
                      Link Existing
                    </Button>
                    {matchedCount === totalCount && totalCount > 0 && (
                      <Button
                        color="indigo"
                        icon={<FontAwesomeIcon icon={faChartPie} />}
                      >
                        Create New
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Summary Stats Row */}
            <div className="flex items-center gap-8 pb-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Total Value
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                  {formatCurrency(
                    portfolio.total_value,
                    portfolio.base_currency
                  )}
                </div>
              </div>
              <div className="h-10 w-px bg-gray-200 dark:bg-gray-700" />
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Holdings
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                  {totalCount}
                </div>
              </div>
              <div className="h-10 w-px bg-gray-200 dark:bg-gray-700" />
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Match Status
                </div>
                <div className="mt-1.5">
                  <MatchStatusBadge matched={matchedCount} total={totalCount} />
                </div>
              </div>
            </div>

            {/* Description */}
            {portfolio.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {portfolio.description}
              </p>
            )}

            {/* Holdings Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Holdings ({totalCount})
                </h3>
                {totalCount >= 5 && (
                  <FormInput
                    placeholder="Search holdings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={<FontAwesomeIcon icon={faSearch} />}
                    iconPosition="start"
                    className="w-72"
                    canClear
                    showClearButton
                    onClear={() => setSearchQuery("")}
                  />
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <HoldingsTable
                  holdings={portfolio.holdings || []}
                  baseCurrency={portfolio.base_currency}
                  onMatchHolding={openMatchDialog}
                  onEditFootnote={openFootnoteDialog}
                  searchQuery={searchQuery}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Need a new portfolio?{" "}
                <OsLink
                  to="/portfolio-builder"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  Create one in Portfolio Builder
                </OsLink>
              </p>
              <Button
                color="red"
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete Definition
              </Button>
            </div>
          </div>
        ) : null}
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Portfolio Definition</DialogTitle>
        <div className="p-4">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to delete this portfolio definition? This
            action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button outline onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Match Holding Dialog */}
      <Dialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
      >
        <DialogTitle>Match Holding to Product</DialogTitle>
        <div className="p-4 space-y-4">
          {/* Selected product display */}
          {selectedProduct ? (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {selectedProduct.display_name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedProduct.isin && (
                      <span className="font-mono">{selectedProduct.isin}</span>
                    )}
                    {selectedProduct.currency && (
                      <span> • {selectedProduct.currency}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            /* Product search */
            <div className="relative">
              <FormInput
                label="Search Products"
                placeholder="Search by name or ISIN..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                icon={
                  productSearchLoading ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )
                }
                iconPosition="start"
              />

              {/* Search results dropdown */}
              {productSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {productSearchResults.map((product) => (
                    <button
                      key={product.resource_id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                        {product.long_name || product.display_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {product.isin && (
                          <span className="font-mono">{product.isin}</span>
                        )}
                        {product.currency && (
                          <span> • {product.currency}</span>
                        )}
                        <span className="capitalize">
                          {" "}
                          • {product.resource_type}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {productSearchQuery.length >= 2 &&
                !productSearchLoading &&
                productSearchResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                    No products found
                  </div>
                )}
            </div>
          )}

          {/* Note/reasoning */}
          {selectedProduct && (
            <div>
              <FormInput
                label="Add a note (recommended)"
                placeholder="e.g., Exact ISIN match verified, or Using as proxy because..."
                value={matchNote}
                onChange={(e) => setMatchNote(e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Explain why this product was matched - helpful for audit trail.
              </p>
            </div>
          )}

          {/* Help text */}
          {!selectedProduct && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Can&apos;t find the product? Add it via the{" "}
              <OsLink
                to="/add-products"
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                Fund Processor
              </OsLink>
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button outline onClick={() => setMatchDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              color="indigo"
              onClick={handleMatchHolding}
              disabled={!selectedProduct || matchLoading}
            >
              {matchLoading ? "Matching..." : "Match Product"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Link Portfolio Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedLinkPortfolio(null);
          setLinkNote("");
        }}
      >
        <DialogTitle>
          {selectedLinkPortfolio
            ? "Confirm Portfolio Link"
            : "Link to Existing Portfolio"}
        </DialogTitle>
        <div className="p-4 space-y-4">
          {!selectedLinkPortfolio ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Search for an existing portfolio to link to this definition.
              </p>

              {/* Portfolio search */}
              <div className="relative">
                <FormInput
                  label="Search Portfolios"
                  placeholder="Search by portfolio name..."
                  value={portfolioSearchQuery}
                  onChange={(e) => setPortfolioSearchQuery(e.target.value)}
                  icon={
                    portfolioSearchLoading ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faSearch} />
                    )
                  }
                  iconPosition="start"
                />

                {/* Search results dropdown */}
                {portfolioSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {portfolioSearchResults.map((p) => (
                      <button
                        key={p.resource_id}
                        onClick={() => handleSelectLinkPortfolio(p)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {p.display_name}
                        </div>
                        {p.currency && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {p.currency}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {portfolioSearchQuery.length >= 2 &&
                  !portfolioSearchLoading &&
                  portfolioSearchResults.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                      No portfolios found
                    </div>
                  )}
              </div>

              {/* Help text */}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Need a new portfolio?{" "}
                <OsLink
                  to="/portfolio-builder"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  Create one in Portfolio Builder
                </OsLink>
              </p>
            </>
          ) : (
            <>
              {/* Confirmation step with selected portfolio and note */}
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faChartPie}
                      className="text-purple-600 dark:text-purple-400"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedLinkPortfolio.display_name}
                    </div>
                    {selectedLinkPortfolio.currency && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedLinkPortfolio.currency}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedLinkPortfolio(null)}
                    className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Change
                  </button>
                </div>
              </div>

              <FormInput
                label="Add a note (optional)"
                placeholder="e.g., Using as proxy for client's actual portfolio..."
                value={linkNote}
                onChange={(e) => setLinkNote(e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Add context for why you&apos;re linking this portfolio -
                helpful for audit trail.
              </p>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              outline
              onClick={() => {
                if (selectedLinkPortfolio) {
                  setSelectedLinkPortfolio(null);
                } else {
                  setLinkDialogOpen(false);
                }
              }}
            >
              {selectedLinkPortfolio ? "Back" : "Cancel"}
            </Button>
            {selectedLinkPortfolio && (
              <Button color="indigo" onClick={handleConfirmLinkPortfolio}>
                Link Portfolio
              </Button>
            )}
          </div>
        </div>
      </Dialog>

      {/* Edit Holding Footnote Dialog */}
      <Dialog
        open={footnoteDialogOpen}
        onClose={() => setFootnoteDialogOpen(false)}
      >
        <DialogTitle>Edit Footnote</DialogTitle>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Add or edit the footnote for this holding. This helps document why a
            match was made.
          </p>
          <FormInput
            label="Footnote"
            placeholder="e.g., Exact ISIN match verified, or Using as proxy because..."
            value={footnoteValue}
            onChange={(e) => setFootnoteValue(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button outline onClick={() => setFootnoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              color="indigo"
              onClick={handleSaveFootnote}
              disabled={footnoteSaving}
            >
              {footnoteSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Edit Portfolio Link Note Dialog */}
      <Dialog
        open={editLinkNoteDialogOpen}
        onClose={() => setEditLinkNoteDialogOpen(false)}
      >
        <DialogTitle>Edit Portfolio Link Note</DialogTitle>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Add or edit the note explaining why this portfolio was linked.
          </p>
          <FormInput
            label="Note"
            placeholder="e.g., Using as proxy for client's actual portfolio..."
            value={editLinkNoteValue}
            onChange={(e) => setEditLinkNoteValue(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button outline onClick={() => setEditLinkNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button color="indigo" onClick={handleSaveLinkNote}>
              Save
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

// ============================================
// Main Portfolios Component
// ============================================

export function Portfolios({
  entityId,
  entityType,
  websocketMessageType,
  disableDrawer = false,
}: PortfoliosProps) {
  const [summaries, setSummaries] = useState<PortfolioDefinitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(
    null
  );
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();

  // Subscribe to WebSocket channel
  useEffect(() => {
    if (websocketMessageType) {
      subscribe(websocketMessageType);
      return () => unsubscribe(websocketMessageType);
    }
  }, [websocketMessageType, subscribe, unsubscribe]);

  // Fetch portfolios
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await AgenticAPI.getAgenticPortfolioDefinitions(entityId);
      setSummaries(data);
    } catch (err) {
      console.error("Error fetching portfolios:", err);
      setError("Failed to load portfolios");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // WebSocket handling
  useEffect(() => {
    if (!lastMessage || !websocketMessageType) return;
    const msg = lastMessage.get(websocketMessageType);
    if (!msg?.payload) return;

    const { operation, full_document } = msg.payload;
    if (!full_document || full_document.pod_instance_id !== entityId) return;

    if (operation === "update" || operation === "insert") {
      fetchData();
    }
  }, [lastMessage, websocketMessageType, entityId, fetchData]);

  // Filter portfolios
  const filteredSummaries = useMemo(() => {
    if (!searchQuery) return summaries;
    const q = searchQuery.toLowerCase();
    return summaries.filter((s) => s.name.toLowerCase().includes(q));
  }, [summaries, searchQuery]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[16rem] flex-1 flex-col items-center justify-center">
        <FontAwesomeIcon
          icon={faSpinner}
          className="h-6 w-6 animate-spin text-slate-400 dark:text-slate-500"
        />
        <span className="ml-2 text-slate-600 dark:text-slate-400">Loading portfolios...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorBanner message={error} />
      </div>
    );
  }

  if (summaries.length === 0) {
    return (
      <div className="flex h-full min-h-[16rem] flex-1 flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 px-4">
        <FontAwesomeIcon
          icon={faBriefcase}
          className="h-12 w-12 mb-4 opacity-50"
        />
        <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
          No portfolios found
        </p>
        <p className="text-sm">
          Portfolio definitions will appear here once extracted
        </p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header with search */}
      <div className="px-6 pt-6 pb-4 shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {filteredSummaries.length} portfolio
            {filteredSummaries.length !== 1 ? "s" : ""}
          </div>
        </div>
        {summaries.length >= 3 && (
          <FormInput
            placeholder="Search portfolios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<FontAwesomeIcon icon={faSearch} />}
            iconPosition="start"
            canClear
            showClearButton
            onClear={() => setSearchQuery("")}
          />
        )}
      </div>

      {/* Portfolio List */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl overflow-hidden">
          <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {filteredSummaries.map((summary) => {
              const matchPercentage =
                summary.holdings_count > 0
                  ? (summary.matched_count / summary.holdings_count) * 100
                  : 0;

              return (
                <motion.div
                  key={summary._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group"
                  onClick={() => {
                    setSelectedPortfolioId(summary._id);
                  }}
                >
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center shrink-0">
                    <FontAwesomeIcon
                      icon={faChartPie}
                      className="text-slate-400 dark:text-slate-500"
                    />
                  </div>

                  {/* Name & meta */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white truncate">
                      {summary.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-3">
                      <span>
                        {formatCurrency(
                          summary.total_value,
                          summary.base_currency
                        )}
                      </span>
                      <span>
                        {summary.holdings_count} holding
                        {summary.holdings_count !== 1 ? "s" : ""}
                      </span>
                      {summary.created_at && (
                        <span>{formatRelativeDate(summary.created_at)}</span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-1.5 w-28 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={clsx(
                          "h-full rounded-full transition-all",
                          matchPercentage === 100
                            ? "bg-emerald-400"
                            : matchPercentage >= 50
                              ? "bg-emerald-400/70"
                              : "bg-amber-400"
                        )}
                        style={{ width: `${matchPercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusBadge
                      status={summary.status}
                      portfolioId={summary.portfolio_id}
                    />
                    <MatchStatusBadge
                      matched={summary.matched_count}
                      total={summary.holdings_count}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filteredSummaries.length === 0 && searchQuery && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <FontAwesomeIcon
                icon={faSearch}
                className="text-2xl mb-3 opacity-30"
              />
              <span className="text-sm">
                No portfolios match &quot;{searchQuery}&quot;
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer */}
      {!disableDrawer && (
        <PortfolioDrawer
          portfolioDefId={selectedPortfolioId}
          onClose={() => setSelectedPortfolioId(null)}
          onDeleted={fetchData}
          onUpdated={fetchData}
        />
      )}
    </div>
  );
}

// Legacy export for backward compatibility
export interface PortfolioTable {
  id: string;
  name: string;
  data: any[];
  portfolio_id?: string;
}
